import { useState } from 'react';
import clsx from 'clsx';

import DeleteModal from '@atom/components/common/DeleteModal';
import RenameModal from '@atom/components/common/RenameModal';
import { Checkbox, Icon, ListTable, Menu } from '@atom/mui';
import {
  DeleteIcon,
  EditIcon,
  FileDownloadIcon,
  StarIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { Media } from '@atom/types/media';
import { FILES_ENDPOINT } from '@atom/utilities/endpoints';
import { downloadFile } from '@atom/utilities/fileUtilities';
import { getMediaIcon } from '@atom/utilities/iconUtilities';
import { isViewableMedia } from '@atom/utilities/mediaUtilities';
import textDisplayUtilities from '@atom/utilities/textDisplayUtilities';
import { setDisplayDate } from '@atom/utilities/timeUtilities';
import { getUserFullName } from '@atom/utilities/userUtilities';

import classes from './elementMedia.module.css';

const { MenuItem } = Menu;
const { TableRow, TableCell } = ListTable;

type RowModal = 'rename' | 'delete' | null;

type ElementMediaTableRowProps = {
  medium: Media;
  checked: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canMarkAsCoverPhoto?: boolean;
  isCoverPhoto?: boolean;
  onToggleCheck: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onOpen: (id: string) => void;
  onCoverPhotoToggle?: (id: string) => void;
};

const ElementMediaTableRow = ({
  medium,
  checked,
  canUpdate,
  canDelete,
  canMarkAsCoverPhoto = false,
  isCoverPhoto = false,
  onToggleCheck,
  onRename,
  onDelete,
  onOpen,
  onCoverPhotoToggle = () => {},
}: ElementMediaTableRowProps) => {
  const [openModal, setOpenModal] = useState<RowModal>(null);

  const fileName = `${medium.name}.${medium.fileExtension}`;
  const viewable = isViewableMedia(medium);
  const fileHref = `${FILES_ENDPOINT}/${medium.fileId}`;
  const openLightbox = () => onOpen(medium.id);

  const nameLink = viewable ? (
    <div
      className={classes.clickableLink}
      onClick={openLightbox}
      data-cy={`elementMediaNameLink-${medium.name}`}
    >
      {fileName}
    </div>
  ) : (
    <a
      className={classes.clickableLink}
      href={fileHref}
      target="_blank"
      rel="noreferrer"
      data-cy={`elementMediaNameLink-${medium.name}`}
    >
      {fileName}
    </a>
  );

  return (
    <TableRow data-cy={`elementMediaTableRow-${medium.name}`}>
      <TableCell padding="checkbox" width="3rem">
        <Checkbox
          checked={checked}
          onChange={() => onToggleCheck(medium.id)}
          data-cy={`elementMediaCheckbox-${medium.name}`}
        />
      </TableCell>
      <TableCell>
        <div className={classes.nameRowContainer}>
          <div
            className={clsx(classes.nameRowIcon, viewable && classes.clickable)}
            onClick={viewable ? openLightbox : undefined}
            data-cy={`elementMediaThumbnail-${medium.name}`}
          >
            {getMediaIcon(
              medium.type,
              medium.fileExtension,
              medium.fileId,
              medium.name,
            )}
          </div>
          {isCoverPhoto && (
            <Icon
              sx={{ marginRight: '0.3125rem' }}
              color={colors.brand.blue}
              icon={StarIcon}
              data-cy={`elementMediaCoverPhotoIndicator-${medium.name}`}
            />
          )}
          {nameLink}
        </div>
      </TableCell>
      <TableCell>{getUserFullName(medium.createdBy)}</TableCell>
      <TableCell>{setDisplayDate(medium.createdDate)}</TableCell>
      <TableCell align="right" width="3rem">
        <Menu dataCyLabel={`elementMediaFileMenu-${medium.name}`}>
          <MenuItem
            startAdornment={<Icon icon={FileDownloadIcon} />}
            onClick={() => downloadFile(fileHref, fileName)}
            data-cy={`elementMediaDownloadButton-${medium.name}`}
          >
            Download
          </MenuItem>
          {canUpdate && (
            <MenuItem
              startAdornment={<Icon icon={EditIcon} />}
              onClick={() => setOpenModal('rename')}
              data-cy={`elementMediaEditButton-${medium.name}`}
            >
              Edit
            </MenuItem>
          )}
          {canMarkAsCoverPhoto && (
            <MenuItem
              startAdornment={
                <Icon
                  color={isCoverPhoto ? colors.brand.blue : undefined}
                  icon={StarIcon}
                />
              }
              onClick={() => onCoverPhotoToggle(medium.id)}
              data-cy={`elementMediaCoverPhotoButton-${medium.name}`}
            >
              {isCoverPhoto ? 'Unmark As Cover Photo' : 'Mark As Cover Photo'}
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem
              startAdornment={<Icon icon={DeleteIcon} />}
              onClick={() => setOpenModal('delete')}
              data-cy={`elementMediaDeleteButton-${medium.name}`}
            >
              Delete
            </MenuItem>
          )}
        </Menu>
        {canUpdate && (
          <RenameModal
            open={openModal === 'rename'}
            closeModal={() => setOpenModal(null)}
            id={medium.id}
            name={medium.name}
            type={medium.type}
            renameAction={onRename}
          />
        )}
        {canDelete && (
          <DeleteModal
            open={openModal === 'delete'}
            title={`Delete ${textDisplayUtilities.capitalize(medium.type)}s`}
            content={`Are you sure you want to delete this ${medium.type}?`}
            onCancel={() => setOpenModal(null)}
            onConfirm={() => {
              onDelete(medium.id);
              setOpenModal(null);
            }}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

export default ElementMediaTableRow;
